/* Promo bar: sections/promo-bar.liquid.
 *
 * 1. Messages sit still when they fit and scroll as a marquee when they don't.
 *    The scroll speed is set in pixels per second, so a longer line takes
 *    longer to go round rather than moving faster.
 * 2. The messages are a button that opens a panel (a modal <dialog>) listing
 *    every message with its products and link. Its rows wait in a <template>,
 *    so a page view that never warms the panel downloads none of its images.
 *    Warming fills the panel (still closed) and starts the downloads: on
 *    hover, touch or keyboard focus on the bar, and, unless the section turns
 *    it off, for the first PRELOAD_COUNT images once the page is idle after
 *    load (not with Save-Data or on a 2g connection).
 * 3. WCAG 2.2.2 (moving content must be possible to stop): the panel's
 *    "Pause scrolling" stops the scroll, and it stays stopped after the
 *    panel closes, remembered for the visitor, until "Resume scrolling".
 *    Opening or closing the panel doesn't change it.
 * 4. On a collection page, MENS / WOMENS carries the shopper's category to
 *    the other gender's collection as ?pb_category=, the active pill's
 *    category filters. The landing page redirects to its own pill with the
 *    same category (layout/theme.liquid, from the pill list that
 *    sections/main-collection-product-grid-v3.liquid prints). Kept current
 *    as pills change by AJAX, and recomputed when a link is pressed.
 * 5. MENS / WOMENS follows the product after a PDP colour swap. The swap
 *    replaces <main> only, so the header's links would otherwise keep pointing
 *    at the previous colour. The new product's links travel in a hidden
 *    [data-gender-links] element inside its product-info
 *    (snippets/gender-links.liquid, mode 'data').
 *
 * The section can be added twice from the editor, so everything sits inside
 * the customElements guard (a second run of this file declares nothing).
 */
if (!customElements.get('promo-bar')) {
  // Until 2026-11-03 opening the panel stored a pause under the old key, so
  // a value there wasn't the shopper's choice: drop it and start over.
  const PAUSE_KEY = 'jnco:promo-bar-paused-v2';
  try {
    window.localStorage.removeItem('jnco:promo-bar-paused');
  } catch (error) {
    /* storage unavailable */
  }
  const PRELOAD_COUNT = 8;

  const slowConnection = () => {
    const connection = navigator.connection;
    if (!connection) return false;
    return Boolean(connection.saveData) || /2g$/.test(connection.effectiveType || '');
  };

  const readPaused = () => {
    try {
      return window.localStorage.getItem(PAUSE_KEY) === '1';
    } catch (error) {
      return false;
    }
  };

  const writePaused = (paused) => {
    try {
      if (paused) window.localStorage.setItem(PAUSE_KEY, '1');
      else window.localStorage.removeItem(PAUSE_KEY);
    } catch (error) {
      /* storage unavailable: the button still works for this page */
    }
  };

  // The filters that say which category a pill shows. Keep in step with the
  // copy in layout/theme.liquid.
  const categoryKey = (query) => {
    const keys = [];
    new URLSearchParams(query).forEach((value, key) => {
      if (key === 'filter.p.m.custom.filters_category' || key === 'filter.p.tag') {
        keys.push(`${key}=${value.toLowerCase()}`);
      }
    });
    return keys.sort().join('|');
  };

  class PromoBar extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-promo-viewport]');
      this.track = this.querySelector('[data-promo-track]');
      this.group = this.querySelector('[data-promo-group]');
      this.gender = this.querySelector('[data-promo-gender]');
      this.panel = this.querySelector('[data-promo-panel]');
      this.motionButton = this.querySelector('[data-promo-motion]');
      this.backdrop = this.querySelector('[data-promo-backdrop]');

      this.setPaused(readPaused());

      if (this.gender && this.gender.hasAttribute('data-collection-page')) {
        this.syncCategory = this.syncCategory.bind(this);
        this.syncCategory();
        document.addEventListener('facets:filters-updated', this.syncCategory);
        window.addEventListener('popstate', this.syncCategory);
        if (document.readyState !== 'complete') window.addEventListener('load', this.syncCategory, { once: true });
        // Pressing a link reads the page as it is right then (a pill the
        // quick-filter script highlighted without a filter event).
        this.gender.addEventListener('pointerdown', this.syncCategory);
        this.gender.addEventListener('focusin', this.syncCategory);
        this.gender.addEventListener('click', this.syncCategory);
      }

      if (this.panel && this.viewport) {
        this.onOpenClick = () => this.openPanel();
        this.onCloseClick = () => this.panel.close();
        this.onMotionClick = () => {
          const paused = !this.classList.contains('promo-bar--paused');
          writePaused(paused);
          this.setPaused(paused);
        };
        // The backdrop covers everything outside the panel, the bar included.
        this.onBackdropClick = () => this.panel.close();
        // A non-modal dialog doesn't close on Escape or keep focus in, so
        // the panel closes on Escape and when focus moves outside it.
        this.onKeydown = (event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            this.panel.close();
          }
        };
        this.onFocusOut = (event) => {
          const next = event.relatedTarget;
          if (next && !this.panel.contains(next) && next !== this.viewport) this.panel.close();
        };
        this.onPanelClose = () => {
          // The close event arrives a frame later; the panel may have been
          // opened again since.
          if (this.panel.open) return;
          this.viewport.setAttribute('aria-expanded', 'false');
          document.documentElement.classList.remove('promo-bar-panel-open');
          if (this.backdrop) this.backdrop.hidden = true;
          window.removeEventListener('resize', this.onResize);
          document.removeEventListener('keydown', this.onKeydown);
          this.panel.removeEventListener('focusout', this.onFocusOut);
          // Focus was in the panel (Escape, the close button, a click on the
          // backdrop): give it back to the bar.
          const active = document.activeElement;
          if (!active || active === document.body || this.panel.contains(active)) {
            this.viewport.focus({ preventScroll: true });
          }
        };
        this.onResize = () => this.placePanel();
        this.onIntent = () => this.warmPanel();
        this.viewport.addEventListener('click', this.onOpenClick);
        this.viewport.addEventListener('pointerenter', this.onIntent);
        this.viewport.addEventListener('pointerdown', this.onIntent);
        this.viewport.addEventListener('focus', this.onIntent);
        if (this.hasAttribute('data-preload-panel') && !slowConnection()) {
          const preload = () => {
            const idle = window.requestIdleCallback || ((callback) => setTimeout(callback, 1));
            idle(() => this.warmPanel(PRELOAD_COUNT), { timeout: 4000 });
          };
          if (document.readyState === 'complete') preload();
          else window.addEventListener('load', preload, { once: true });
        }
        this.panel.querySelector('[data-promo-close]').addEventListener('click', this.onCloseClick);
        if (this.motionButton) this.motionButton.addEventListener('click', this.onMotionClick);
        if (this.backdrop) this.backdrop.addEventListener('click', this.onBackdropClick);
        this.panel.addEventListener('close', this.onPanelClose);
      }

      if (this.viewport && this.group) {
        // Measured now (the section's stylesheet loads before this deferred
        // script runs), then whenever the bar or the messages change size.
        // iOS Safari did not deliver the observer's first callback when
        // tested (Simulator, 26.5), so load and the web fonts arriving are
        // also measured on their own.
        this.measure = this.measure.bind(this);
        this.measure();
        this.resizeObserver = new ResizeObserver(this.measure);
        this.resizeObserver.observe(this.viewport);
        this.resizeObserver.observe(this.group);
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.measure);
        if (document.readyState !== 'complete') window.addEventListener('load', this.measure, { once: true });
      }
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.syncCategory) {
        document.removeEventListener('facets:filters-updated', this.syncCategory);
        window.removeEventListener('popstate', this.syncCategory);
      }
      if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);
      if (this.panel && this.panel.open) this.panel.close();
      if (this.onOpenClick) this.viewport.removeEventListener('click', this.onOpenClick);
    }

    setPaused(paused) {
      this.classList.toggle('promo-bar--paused', paused);
      if (!this.motionButton) return;
      this.motionButton.textContent = paused
        ? this.motionButton.dataset.labelPlay
        : this.motionButton.dataset.labelPause;
    }

    // The panel hangs from the bar's bottom edge (or the top of the screen
    // when the bar has scrolled away).
    placePanel() {
      const bottom = Math.max(0, Math.round(this.getBoundingClientRect().bottom));
      // On the bar, so the panel and the backdrop both inherit it.
      this.style.setProperty('--pb-panel-top', `${bottom}px`);
    }

    // Moves the rows out of the template into the (closed) panel, once.
    fillPanel() {
      if (this.panelFilled) return;
      this.panelFilled = true;
      const body = this.panel.querySelector('[data-promo-panel-body]');
      const template = this.panel.querySelector('[data-promo-panel-template]');
      if (!template || !body) return;
      const rows = template.content.cloneNode(true);
      rows.querySelectorAll('.promo-bar__tile-image').forEach((image) => {
        const show = () => image.classList.add('is-loaded');
        if (image.complete && image.naturalWidth) show();
        else image.addEventListener('load', show, { once: true });
      });
      body.appendChild(rows);
    }

    // Starts the downloads: the tile images render loading="lazy", and a lazy
    // image in a closed dialog never loads, so they switch to eager. With a
    // limit (the idle preload) only the first few, at low priority.
    warmPanel(limit = Infinity) {
      if (!this.panel) return;
      this.fillPanel();
      const images = this.panel.querySelectorAll('.promo-bar__tile-image[loading="lazy"]');
      Array.from(images)
        .slice(0, limit)
        .forEach((image) => {
          if (limit !== Infinity) image.fetchPriority = 'low';
          image.loading = 'eager';
        });
    }

    openPanel() {
      if (this.panel.open) return;
      this.warmPanel();
      // Opening and closing the panel leave the scroll as it is; the panel's
      // button is the stop control.
      if (this.motionButton) {
        this.motionButton.hidden = !this.classList.contains('promo-bar--scrolling');
      }
      this.placePanel();
      window.addEventListener('resize', this.onResize);
      this.viewport.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('promo-bar-panel-open');
      // show(), not showModal(): a modal goes to the top layer, above the
      // Alia popup. The section's own layer keeps it under that popup.
      if (this.backdrop) this.backdrop.hidden = false;
      this.panel.show();
      document.addEventListener('keydown', this.onKeydown);
      this.panel.addEventListener('focusout', this.onFocusOut);
      // Through the theme's focus(), so a tap leaves no focus ring.
      const close = this.panel.querySelector('[data-promo-close]');
      if (close) close.focus({ preventScroll: true });
    }

    // The width of the messages themselves, from the first to the last one.
    // Measured this way (not the group's width) so it doesn't change when the
    // scrolling state adds the closing separator and padding.
    messagesWidth() {
      const messages = this.group.querySelectorAll('.promo-bar__message');
      if (!messages.length) return 0;
      const first = messages[0].getBoundingClientRect();
      const last = messages[messages.length - 1].getBoundingClientRect();
      return last.right - first.left;
    }

    measure() {
      const available = this.viewport.clientWidth;
      if (!available) return;
      const overflows = this.messagesWidth() > available + 1;
      const scrolling = this.classList.contains('promo-bar--scrolling');

      if (overflows && !scrolling) {
        const copy = this.group.cloneNode(true);
        copy.removeAttribute('data-promo-group');
        copy.setAttribute('aria-hidden', 'true');
        copy.querySelectorAll('[data-shopify-editor-block]').forEach((node) => {
          node.removeAttribute('data-shopify-editor-block');
        });
        copy.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((node) => {
          node.setAttribute('tabindex', '-1');
        });
        copy.setAttribute('data-promo-copy', '');
        this.track.appendChild(copy);
        this.classList.add('promo-bar--scrolling');
      } else if (!overflows && scrolling) {
        this.track.querySelectorAll('[data-promo-copy]').forEach((node) => node.remove());
        this.classList.remove('promo-bar--scrolling');
      }

      if (overflows) {
        // One loop is the group's full width (messages, closing separator and
        // the gap before the copy), at the set speed.
        const speed = Number(this.dataset.speed) || 40;
        const distance = this.group.getBoundingClientRect().width;
        this.style.setProperty('--pb-duration', `${(distance / speed).toFixed(2)}s`);
      }
    }

    syncCategory() {
      const pill = document.querySelector('[data-quick-filter-btn][aria-pressed="true"]');
      const key = pill ? categoryKey(pill.dataset.query || '') : '';
      this.gender.querySelectorAll('[data-gender]').forEach((link) => {
        if (link.dataset.baseHref === undefined) link.dataset.baseHref = link.getAttribute('href') || '';
        const base = link.dataset.baseHref;
        if (!base) return;
        const separator = base.includes('?') ? '&' : '?';
        link.setAttribute('href', key ? `${base}${separator}pb_category=${encodeURIComponent(key)}` : base);
      });
    }

    updateGender(source) {
      if (!this.gender) return;
      const current = source ? source.dataset.current : '';
      if (current !== 'men' && current !== 'women') {
        this.gender.hidden = true;
        this.classList.add('promo-bar--no-gender');
        return;
      }
      this.gender.querySelectorAll('[data-gender]').forEach((link) => {
        const gender = link.dataset.gender;
        link.setAttribute('href', gender === 'men' ? source.dataset.menUrl : source.dataset.womenUrl);
        if (gender === current) link.setAttribute('aria-current', 'page');
        else link.removeAttribute('aria-current');
      });
      this.gender.hidden = false;
      this.classList.remove('promo-bar--no-gender');
    }
  }

  customElements.define('promo-bar', PromoBar);

  // product-info dispatches this from connectedCallback, so it fires for the
  // swapped-in product too. Quick add's copy carries data-update-url="false"
  // and is not the page's product, so it is ignored.
  document.addEventListener('product-info:loaded', (event) => {
    const info = event.target;
    if (!(info instanceof HTMLElement) || info.dataset.updateUrl !== 'true') return;
    if (!info.closest('#MainContent')) return;
    const source = info.querySelector('[data-gender-links]');
    if (!source) return;
    document.querySelectorAll('promo-bar').forEach((bar) => bar.updateGender(source));
  });
}
